import { useState } from "react";
import { Button } from "components/Button/Button";
import { compareCode, handleDifference } from "lib/parameters/code";
import { resultStatusError } from "lib/results/status";
import { QueryResult } from "lib/results/types";
import { useAnalytics } from "gui/analytics/analytics";
import { Icon } from "gui/icon/icon";
import { useSession } from "gui/session/session";
import { debugWandQuery } from "./context";
import {
  useEditorDispatch,
  useEditorParameters,
  useEditorQueryCode,
  useEditorQueryHasEditPermission,
} from "../context";

export function FixQuery(props: { result: QueryResult | undefined }) {
  const [isFetchingFixQuery, setIsFetchingFixQuery] = useState(false);
  const session = useSession();
  const dispatch = useEditorDispatch();
  const code = useEditorQueryCode();
  const parameters = useEditorParameters();
  const canEdit = useEditorQueryHasEditPermission();
  const { captureEvent } = useAnalytics();

  if (session === undefined) {
    return null;
  }

  if (!canEdit) {
    return null;
  }

  const statusError = resultStatusError(props.result);
  const parsedError = statusError?.split("[Execution ID")?.[0];

  if (parsedError === undefined) {
    return null;
  }

  const parsedErrorHasLineNumber =
    parsedError.includes("Line") || parsedError.includes("line");

  if (!parsedErrorHasLineNumber) {
    return null;
  }

  const updateQueryCodeAndParameters = (nextCode: string) => {
    const { difference, nextKeys, prevKeys } = compareCode(code, nextCode);

    if (parameters) {
      const { nextParameters, updatedCode } = handleDifference({
        difference,
        nextCode,
        nextKeys,
        parameters,
        prevKeys,
      });

      dispatch({
        type: "replaceQueryCodeAndParameters",
        parameters: nextParameters,
        code: updatedCode ?? nextCode,
      });
    }
  };

  const handleButtonClick = async () => {
    captureEvent("Query editor: LLM Debug query");
    setIsFetchingFixQuery(true);

    try {
      const result = await debugWandQuery(code, parsedError, session);

      if (result === undefined) {
        return;
      }

      updateQueryCodeAndParameters(result.query);
      document.getElementById("run-query-button")?.click();
    } catch (error) {
      captureEvent("Query editor: LLM Debug query failed");
    } finally {
      setIsFetchingFixQuery(false);
    }
  };

  return (
    <Button
      theme="primary"
      size="M"
      loading={isFetchingFixQuery}
      onClick={handleButtonClick}
    >
      <Icon icon="wand" />
      Fix Query
    </Button>
  );
}
